import { useState, type JSX } from 'react'
import type { SoftwarePlMonth } from '../api'
import { isNegative, monthLabel, yen } from '../format'
import { CHART_INK, POLARITY, SERIES } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { NoDataZone } from '../charts/NoDataZone'
import { monthsAfter } from '../timeWindow'
import { PlanBadge } from './PlanToggle'
import { SourceNote } from './FreshnessStatus'

// Is the software team paying for itself yet? Two lines over time — software
// INCOME and the fully-loaded software-team COST — so you can read both absolute
// levels and watch them converge. Breakeven is where income rises to meet cost
// (the lines cross); the shaded gap between them is the monthly margin, coral
// while cost is still above income (subsidised) and blue once income clears it.
//
// Why two lines, not one bar of the margin: the question is a comparison of two
// quantities trending toward each other, and the two quantities are exactly what
// you want to see. A bar of the difference hides them both, and — the tell that
// prompted this redesign — hides a cost that has silently gone to zero. Here a
// zero cost is a flat line on the floor you can't miss.
//
// Colours: blue is the income you came to read, yellow the cost to tell apart
// from it (the one validated two-series pair on this surface, palette.ts); the
// gap uses polarity, never green/red, and the sign is also carried by which line
// sits on top. Cost is NULL when a single-worker month would make the aggregate
// personally identifying (invariant #5): the cost line breaks and the gap drops
// out for that month — income still shows.

const W = 720
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)

type Row = { month: SoftwarePlMonth; revenue: number; cost: number | null }

export function BreakevenChart({
  months,
  sourceNote,
  windowEnd,
}: {
  months: SoftwarePlMonth[]
  /** Set when a source behind this chart is out of date; see freshness.ts. */
  sourceNote?: string | null
  /** Last month (YYYY-MM-01) of the page's time window; see NoDataZone. */
  windowEnd: string
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null)

  const rows: Row[] = months
    .map((month) => ({ month, revenue: plotValue(month.softwareRevenue), cost: plotValue(month.softwareCost) }))
    .filter((row): row is Row => row.revenue !== null)

  if (rows.length === 0) {
    return (
      <section className="panel">
        <h2>Software breakeven</h2>
        <SourceNote text={sourceNote ?? null} />
        <p className="notice">
          No software deals or software-team costs in range yet, so there is nothing to compare.
        </p>
      </section>
    )
  }

  // Both series are money amounts ≥ 0, so the domain runs from the zero baseline
  // up; breakeven is a crossing of the two lines, not the zero line.
  const values = [0, ...rows.map((r) => r.revenue), ...rows.flatMap((r) => (r.cost === null ? [] : [r.cost]))]
  const { min, max } = domainFor(values)
  // Months the window covers past THIS chart's last row keep their place on the
  // axis. The software P&L can end earlier than the cash flow, so each chart
  // reads its own end rather than sharing one.
  const lastMonth = rows[rows.length - 1]?.month.month ?? windowEnd
  const gap = monthsAfter(lastMonth, windowEnd)
  const band = PLOT_W / (rows.length + gap.length)
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const centre = (index: number): number => PAD.left + band * index + band / 2

  const revenueLine = rows.map((row, index) => `${centre(index)},${y(row.revenue)}`).join(' ')

  // The cost line and the gap band both break at suppressed months, so build
  // them from maximal runs of consecutive months that have a cost.
  const costRuns: number[][] = []
  rows.forEach((row, index) => {
    if (row.cost === null) return
    const run = costRuns[costRuns.length - 1]
    if (run && run[run.length - 1] === index - 1) run.push(index)
    else costRuns.push([index])
  })

  // The gap band, one region per adjacent pair within a run. Where income and
  // cost cross inside a segment (the breakeven moment the chart is FOR), a single
  // quad would wind into a bow-tie and the sign would be wrong on one side — so
  // split at the crossing into two triangles, each coloured by its own side's
  // sign. (Geometry only: plotValue floats position shapes, never a shown
  // figure.) A crossing is where margin changes sign at t = mL / (mL − mR).
  type Gap = { key: string; points: string; positive: boolean }
  const gaps: Gap[] = []
  for (const run of costRuns) {
    for (let k = 1; k < run.length; k += 1) {
      const iL = run[k - 1] as number
      const iR = run[k] as number
      const a = rows[iL] as Row & { cost: number }
      const b = rows[iR] as Row & { cost: number }
      const xL = centre(iL)
      const xR = centre(iR)
      const mL = a.revenue - a.cost
      const mR = b.revenue - b.cost
      if ((mL >= 0) === (mR >= 0)) {
        gaps.push({
          key: `gap-${iR}`,
          points: `${xL},${y(a.revenue)} ${xR},${y(b.revenue)} ${xR},${y(b.cost)} ${xL},${y(a.cost)}`,
          positive: mL + mR >= 0,
        })
      } else {
        const t = mL / (mL - mR)
        const xc = xL + t * (xR - xL)
        const yc = y(a.revenue) + t * (y(b.revenue) - y(a.revenue)) // == the cost line at t (margin 0)
        gaps.push({ key: `gap-${iR}-l`, points: `${xL},${y(a.revenue)} ${xc},${yc} ${xL},${y(a.cost)}`, positive: mL >= 0 })
        gaps.push({ key: `gap-${iR}-r`, points: `${xc},${yc} ${xR},${y(b.revenue)} ${xR},${y(b.cost)}`, positive: mR >= 0 })
      }
    }
  }

  // The header describes the MOST RECENT month, never an older one that happens
  // to have a cost — labelling stale data as current is the disease this project
  // kills. The margin is read from the authoritative softwareMargin STRING (the
  // view computes it), never re-derived from floats, so it can't drift from the
  // tooltip or flip sign at breakeven.
  const latest = rows[rows.length - 1] ?? null
  const active = hover === null ? null : (rows[hover] ?? null)
  const hoveredGap = hover === null ? null : (gap[hover - rows.length] ?? null)

  return (
    <section className="panel chart-panel">
      <header className="chart-head">
        <div>
          <h2>Software breakeven</h2>
          <SourceNote text={sourceNote ?? null} />
          <p className="chart-sub">
            Software income against the fully-loaded software-team cost. The team breaks even where
            income meets cost; the gap is how far it has to go.
            {latest ? (
              latest.cost !== null ? (
                <>
                  {' '}Latest ({monthLabel(latest.month.month)}): income <b>{yen(latest.month.softwareRevenue)}</b> vs
                  cost <b>{yen(latest.month.softwareCost)}</b> —{' '}
                  {isNegative(latest.month.softwareMargin) ? (
                    <>subsidised by <b>{yen((latest.month.softwareMargin ?? '').replace(/^-/, ''))}</b>.</>
                  ) : (
                    'paying for itself.'
                  )}
                </>
              ) : (
                <>
                  {' '}Latest ({monthLabel(latest.month.month)}): income{' '}
                  <b>{yen(latest.month.softwareRevenue)}</b>; cost hidden for privacy this month.
                </>
              )
            ) : null}
          </p>
        </div>
        <div className="chart-controls">
          {/* No plan reaches this chart — both series are reported figures — so
              it carries a fixed badge where the forecast charts carry the
              plan switch. Saying "actuals only" outright is the point: the
              reader should never have to work out which charts a plan moves. */}
          <PlanBadge>Actuals only · no plan input</PlanBadge>
          <ul className="legend">
            <li>
              <span className="swatch line" style={{ background: SERIES.blue }} /> Software income
            </li>
            <li>
              <span className="swatch line" style={{ background: SERIES.yellow }} /> Software-team
              cost
            </li>
          </ul>
        </div>
      </header>

      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label="Software income and software-team cost by month, as two lines. Where they cross the software team breaks even; the shaded gap between them is the monthly margin. The figures are in the tooltip and the software P&L table."
          onMouseLeave={() => setHover(null)}
        >
          {gap.length > 0 && (
            <NoDataZone
              x={PAD.left + band * rows.length}
              width={band * gap.length}
              top={PAD.top}
              height={PLOT_H}
              lastMonth={lastMonth}
            />
          )}

          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          {gaps.map((gap) => (
            <polygon
              key={gap.key}
              points={gap.points}
              fill={gap.positive ? POLARITY.positive : POLARITY.negative}
              fillOpacity={0.16}
              stroke="none"
            />
          ))}

          {/* Cost line: one polyline per run so it breaks at suppressed months. */}
          {costRuns.map((run) => (
            <polyline
              key={`cost-${run[0]}`}
              points={run.map((index) => `${centre(index)},${y(rows[index]?.cost ?? 0)}`).join(' ')}
              fill="none"
              stroke={SERIES.yellow}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          <polyline
            points={revenueLine}
            fill="none"
            stroke={SERIES.blue}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {rows.map((row, index) => (
            <g key={`pt-${row.month.month}`}>
              <circle
                cx={centre(index)}
                cy={y(row.revenue)}
                r={hover === index ? 5 : 3}
                fill={SERIES.blue}
                stroke="#272C47"
                strokeWidth={2}
              />
              {row.cost !== null && (
                <circle
                  cx={centre(index)}
                  cy={y(row.cost)}
                  r={hover === index ? 5 : 3}
                  fill={SERIES.yellow}
                  stroke="#272C47"
                  strokeWidth={2}
                />
              )}
            </g>
          ))}

          {[...rows.map((row) => row.month.month), ...gap].map((month, index) => (
            <text
              key={`x-${month}`}
              x={centre(index)}
              y={LABEL_Y}
              className="tick"
              textAnchor="end"
              transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}
            >
              {monthLabel(month)}
            </text>
          ))}

          {rows.map((row, index) => (
            <rect
              key={`hit-${row.month.month}`}
              x={PAD.left + band * index}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
            />
          ))}
          {gap.map((month, offset) => (
            <rect
              key={`hit-${month}`}
              x={PAD.left + band * (rows.length + offset)}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(rows.length + offset)}
            />
          ))}

          {hover !== null && (
            <line
              x1={centre(hover)}
              x2={centre(hover)}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              stroke={CHART_INK.axis}
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
        </svg>

        {hover !== null && hoveredGap !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(hoveredGap)}</strong>
            <span className="tooltip-note">No data for this month</span>
          </div>
        )}

        {hover !== null && active !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(active.month.month)}</strong>
            <span>
              Software income <b>{yen(active.month.softwareRevenue)}</b>
            </span>
            <span>
              Software cost <b>{yen(active.month.softwareCost)}</b>
            </span>
            <span>
              Margin <b>{yen(active.month.softwareMargin)}</b>
            </span>
            {active.cost === null && <span className="tooltip-note">cost hidden for privacy this month</span>}
          </div>
        )}
      </div>
    </section>
  )
}
